import React, { useState, useEffect } from 'react';
import { Disease, DiseaseGroup } from '../../types/diseases';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';

interface CreateDiseaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<Disease>) => void;
  groups: DiseaseGroup[];
  initialData?: Disease;
}

export function CreateDiseaseModal({
  isOpen,
  onClose,
  onSubmit,
  groups,
  initialData,
}: CreateDiseaseModalProps) {
  const [name, setName] = useState('');
  const [groupId, setGroupId] = useState<number | ''>('');
  const [cause, setCause] = useState('');
  const [symptoms, setSymptoms] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [treatments, setTreatments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initialData) {
      setName(initialData.name);
      setGroupId(initialData.groupId);
      setCause(initialData.cause || '');
      setSymptoms(initialData.symptoms || '');
      setDiagnosis(initialData.diagnosis || '');
      setTreatments(initialData.treatments || '');
    } else {
      setName('');
      setGroupId('');
      setCause('');
      setSymptoms('');
      setDiagnosis('');
      setTreatments('');
    }
  }, [initialData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!groupId) {
      setError('Disease group is required');
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit({
        name,
        groupId: groupId as number,
        cause,
        symptoms,
        diagnosis,
        treatments,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-2xl">
      <BrandModalHeader
        title={initialData ? 'Edit Disease' : 'Create Disease'}
        onClose={onClose}
      />
      <form onSubmit={handleSubmit}>
        <BrandModalBody className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700">
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700">
              Disease Group
            </label>
            <select
              value={groupId}
              onChange={(e) => setGroupId(e.target.value ? Number(e.target.value) : '')}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            >
              <option key="empty-option-1753461297299" value="">Select a group</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700">
              Cause
            </label>
            <textarea
              value={cause}
              onChange={(e) => setCause(e.target.value)}
              rows={3}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700">
              Symptoms
            </label>
            <textarea
              value={symptoms}
              onChange={(e) => setSymptoms(e.target.value)}
              rows={3}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700">
              Diagnosis
            </label>
            <textarea
              value={diagnosis}
              onChange={(e) => setDiagnosis(e.target.value)}
              rows={3}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700">
              Treatments
            </label>
            <textarea
              value={treatments}
              onChange={(e) => setTreatments(e.target.value)}
              rows={3}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          {error && (
            <div className="text-red-600 text-sm">{error}</div>
          )}
        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn btn-primary text-sm disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : initialData ? 'Update' : 'Create'}
          </button>
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
}
